import ErrorMessage from "@/v2-ui/ErrorMessage"
import FeatureNotice from "@/v2-backoffice-client/feature/FeatureNotice"
import { CustomerFeatureAccessSkeleton } from "@/v2-backoffice-client/customer/featureAccess/CustomerFeatureAccess"
import CustomerFeatureAccessComposer from "@/v2-backoffice-client/customer/featureAccess/CustomerFeatureAccessComposer"
import { useCustomerFeatureAccessQuery } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.query"
import { useCustomerFeatureAccessUserCodesQuery } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.userCodes.query"
import LicenseFeatureGroupComposeSkeleton from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeSkeleton"
import { useLicenseSeatHoldersQuery } from "@/v2-backoffice-client/license/seats/license.seats.holders.query"
import UserFeatureAccessHeadline from "@/v2-backoffice-client/user/featureAccess/UserFeatureAccessHeadline"
import { formatUserFirstName } from "@/v2-backoffice-client/user/user.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

export type UserFeatureAccessProps = {
  user: Pick<
    UserFragment,
    "Id" | "FirstName" | "Email" | "CustomerId" | "Customer"
  >
}

// The user panel's What they get tab: the customer tab's answer, read for
// this one person. Access is decided per customer (free for everyone,
// what their licenses and add-ons include, the customer's own list) and
// then per person, where a seated license names who holds a seat; so the
// tab reads the customer's access and the entitlement rule's answer for
// this user, and shows the package editor through the person lens: every
// row leads with whether it reaches them and why, nothing writes. A user
// no customer owns gets what is free for everyone and nothing else; a
// staff user gets everything, so neither needs the lists.
function UserFeatureAccess(props: UserFeatureAccessProps) {
  const { user } = props
  const userName = formatUserFirstName(user)
  const customerId = user.CustomerId ?? null
  const customerName = user.Customer?.Name ?? "their customer"

  const accessQuery = useCustomerFeatureAccessQuery({
    variables: { customerId: customerId ?? 0 },
    skip: customerId === null,
  })
  const access = (accessQuery.data ?? accessQuery.previousData)
    ?.getCustomerFeatureAccess

  const codesQuery = useCustomerFeatureAccessUserCodesQuery({
    variables: { customerId: customerId ?? 0, userId: user.Id },
    skip: customerId === null || access?.IsStaff === true,
  })
  const codes = (codesQuery.data ?? codesQuery.previousData)
    ?.getCustomerFeatureAccessUserCodes

  // Who holds a seat: the one read where this person differs from the
  // customer. Skipped when nobody at the customer can differ; undefined
  // until read, so the headline never calls a holder seatless meanwhile.
  const seatHoldersQuery = useLicenseSeatHoldersQuery({
    variables: { customerId: customerId ?? 0 },
    skip: customerId === null || !access?.UsersMayDiffer,
  })
  const seatHolders = access?.UsersMayDiffer
    ? seatHoldersQuery.data?.getLicenseSeatHolders
    : []

  if (customerId === null) {
    return (
      <FeatureNotice tone="info">
        {userName} belongs to no customer, so no license, add-on or
        customer&apos;s own list reaches them: they get what is free for
        everyone and nothing else. Set the owning customer with Update, above,
        to change that.
      </FeatureNotice>
    )
  }

  const error = accessQuery.error ?? codesQuery.error
  if (error) return <ErrorMessage error={error} />
  if (!access) {
    return accessQuery.loading ? <CustomerFeatureAccessSkeleton /> : null
  }

  if (access.IsStaff) {
    return (
      <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
        <p>
          Staff. {userName} is at Placepoint itself, whose users get every
          feature there is or ever will be, package-only or not, listed or not,
          so nothing in the catalog can block development. There is nothing to
          list per feature.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <UserFeatureAccessHeadline
        userId={user.Id}
        userName={userName}
        access={access}
        customerName={customerName}
        externalCustomerId={user.Customer?.ExternalCustomerId}
        seatHolders={seatHolders}
      />
      {codes ? (
        <CustomerFeatureAccessComposer
          // Reseed when what is persisted changes under the view (another
          // operator's save through the evict), as the customer tab does.
          key={`${access.OwnList?.Id ?? 0}#${access.ClosedFeatureTypeCodes.join()}#${access.OwnList?.FeatureCount ?? 0}#${access.OwnList?.RuleCount ?? 0}`}
          customerName={customerName}
          access={access}
          userCheck={{ userName, codes }}
          isReadOnly
        />
      ) : (
        // The rows are this person's answers, so they wait for the rule's
        // read rather than paint as the customer's and then flip.
        <LicenseFeatureGroupComposeSkeleton withViewSwitch />
      )}
    </div>
  )
}

export default UserFeatureAccess
